import { DateRangePicker } from "@/shared/ui/DateRangePicker";
import { zodResolver } from "@hookform/resolvers/zod";
import CloseIcon from "@mui/icons-material/Close";
import {
  Autocomplete,
  Box,
  Divider,
  Drawer,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import { type MouseEvent, useEffect } from "react";
import { useForm } from "react-hook-form";

import { SelectReceptionStatus } from "@/pages/shipper-cargo-list/ui/SearchForms/select-reception-status";
import { BaseButton } from "@/shared/ui/BaseButton";
import { BaseFormLabel } from "@/shared/ui/base-form-label";
import { BaseTextField } from "@/shared/ui/base-text-field";
import type { SearchFormCardProps } from "../index";
import { LocationSelect } from "../location-select";
import {
  OfferCargoSearchFormSchema,
  type OfferCargoSearchFormSchemaType,
} from "../schema";

type Props = {
  open: boolean;
  onClose: () => void;
} & SearchFormCardProps;

export const MobileSearchForms = ({
  open,
  onClose,
  onGetOffersCargosParamsChange,
  onGetOffersCargosParamsReset,
  companies,
  companyNameFilter,
  onCompanyNameFilterChange,
  onReceptionStatusFilterChange,
  receptionStatusFilter,
  getDraftOffersCargosParams,
}: Props) => {
  const { handleSubmit, setValue, watch, reset } =
    useForm<OfferCargoSearchFormSchemaType>({
      resolver: zodResolver(OfferCargoSearchFormSchema),
    });

  useEffect(() => {
    if (!getDraftOffersCargosParams) {
      return;
    }
    reset({
      ...getDraftOffersCargosParams,
      companyName: companyNameFilter,
      receptionStatus: receptionStatusFilter,
    });
  }, [
    getDraftOffersCargosParams,
    companyNameFilter,
    receptionStatusFilter,
    reset,
  ]);

  const currentValue = watch();

  const handleSearchClick = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    handleSubmit((data) => {
      const { companyName, receptionStatus, ...rest } = data;
      onGetOffersCargosParamsChange(rest);
      onReceptionStatusFilterChange(receptionStatus);
      onCompanyNameFilterChange(companyName ?? "");
    })();

    // NOTE: 検索パラメーターをクエリで保持するようになったら閉じなくて良くなる
    onClose();
  };

  const handleResetClick = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    onGetOffersCargosParamsReset();
    onCompanyNameFilterChange("");
    reset();
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      anchor="bottom"
      sx={{ display: { xs: "block", sm: "none" } }}
      PaperProps={{
        sx: {
          borderTopLeftRadius: "16px",
          borderTopRightRadius: "16px",
          height: "95vh",
        },
      }}
    >
      <Stack
        direction="column"
        sx={{
          borderRadius: "16px",
          flex: 1,
          overflowY: "hidden",
        }}
      >
        {/* ヘッダー部分 */}
        <Stack
          direction="row"
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            position: "relative",
            height: "48px",
            flexShrink: 0,
          }}
        >
          <Typography fontSize={18} fontWeight={700}>
            フィルター
          </Typography>
          <IconButton
            onClick={onClose}
            sx={{
              position: "absolute",
              right: 0,
              top: 0,
              width: "48px",
              height: "48px",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              color: "primary.400",
            }}
          >
            <CloseIcon width={24} height={24} />
          </IconButton>
        </Stack>

        <Divider />

        {/* フォーム部分 */}
        <Box sx={{ flex: 1, overflowY: "auto" }}>
          <Stack spacing={3} p={2} flex={1} alignItems="stretch">
            <DateRangePicker
              label="積日"
              startDate={currentValue.loadingOnFrom}
              endDate={currentValue.loadingOnTo}
              onStartDateChange={(date) => setValue("loadingOnFrom", date)}
              onEndDateChange={(date) => setValue("loadingOnTo", date)}
            />

            <LocationSelect
              label="積地"
              prefecture={currentValue.loadingPrefecture}
              city={currentValue.loadingAddress}
              onPrefectureChange={(prefecture) =>
                setValue("loadingPrefecture", prefecture)
              }
              onCityChange={(city) => setValue("loadingAddress", city)}
            />

            <DateRangePicker
              label="卸日"
              startDate={currentValue.unloadingOnFrom}
              endDate={currentValue.unloadingOnTo}
              onStartDateChange={(date) => setValue("unloadingOnFrom", date)}
              onEndDateChange={(date) => setValue("unloadingOnTo", date)}
            />

            <LocationSelect
              label="卸地"
              prefecture={currentValue.unloadingPrefecture}
              city={currentValue.unloadingAddress}
              onPrefectureChange={(prefecture) =>
                setValue("unloadingPrefecture", prefecture)
              }
              onCityChange={(city) => setValue("unloadingAddress", city)}
            />

            <BaseFormLabel label="荷主名" sx={{ flexGrow: 1 }}>
              <Autocomplete
                options={
                  companies.status === "success"
                    ? companies.data.map((c) => c.name)
                    : []
                }
                freeSolo
                inputValue={currentValue.companyName ?? ""}
                onInputChange={(_, value) => setValue("companyName", value)}
                renderInput={(params) => (
                  <BaseTextField {...params} size="small" />
                )}
              />
            </BaseFormLabel>

            <Stack direction="row" spacing={2}>
              <BaseFormLabel label="受付状況">
                <SelectReceptionStatus
                  value={currentValue.receptionStatus}
                  onChange={(v) => {
                    setValue("receptionStatus", v);
                  }}
                />
              </BaseFormLabel>
            </Stack>
          </Stack>
        </Box>

        <Divider sx={{ mt: 4 }} />

        {/* ボタン部分 */}
        <Stack direction="row" spacing={1.5} px={2} py={3} flexGrow={0}>
          <BaseButton
            size="large"
            variant="outlined"
            onClick={handleResetClick}
            sx={{ flex: 1, minWidth: "auto" }}
          >
            リセット
          </BaseButton>

          <BaseButton
            size="large"
            variant="contained"
            onClick={handleSearchClick}
            sx={{ flex: 1, minWidth: "auto" }}
          >
            絞り込む
          </BaseButton>
        </Stack>
      </Stack>
    </Drawer>
  );
};
